import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";

import type { DesignerThemeNames } from "@skilitics/designer-theme";

import { layersPanelWidthAtom } from "../triggers/response-editor/atoms.ts";
import type { PreviewAuthSetting } from "./types.ts";

const ROOT = "application";
const atomWithStorageOptions = { getOnInit: true };

export const defaultTutorialsState = {
  moduleMap: true,
  scene: true,
  activity: true,
  responses: true,
};

export const hidePanelsAtom = atom(false);
export const keyboardShortcutsVisibleAtom = atom(false);

// The composable canvas's layers/components/library panel visibility. These live here (rather than
// with the canvas) so the thumbnail nav bar can derive how much of its start edge the panel covers.
export const showLayersAtom = atomWithStorage<boolean>(
  "composable_showLayers",
  true,
  undefined,
  atomWithStorageOptions,
);
export const showComponentsAtom = atomWithStorage<boolean>(
  "composable_showComponents",
  true,
  undefined,
  atomWithStorageOptions,
);
export const showLibraryPanelAtom = atom<boolean>(false); // library pane is transient.

// A composable canvas renders its layers/components/library panel over the start of the thumbnail
// nav bar. Reserve that covered width so the thumbnails stay centred in the space that remains.
export const composablePanelReservedWidthAtom = atom((get) => {
  const panelShown =
    !get(hidePanelsAtom) && (get(showLayersAtom) || get(showComponentsAtom) || get(showLibraryPanelAtom));
  return panelShown ? get(layersPanelWidthAtom) : "0px";
});

// Local storage atoms
export const previewAuthAtom = atomWithStorage<PreviewAuthSetting>(
  `${ROOT}_previewAuth`,
  "none",
  undefined,
  atomWithStorageOptions,
);

export const darkModeAtom = atomWithStorage(`${ROOT}_darkMode`, false, undefined, atomWithStorageOptions);

export const enableConsoleAtom = atomWithStorage(`${ROOT}_enableConsole`, false, undefined, atomWithStorageOptions);

export const activeDesignerThemeNameAtom = atomWithStorage<DesignerThemeNames>(
  `${ROOT}_themeName`,
  "light",
  undefined,
  atomWithStorageOptions,
);

export const collapseThumbnailNavigationBar = atomWithStorage<boolean>(
  `${ROOT}_collapseThumbnailBar`,
  false,
  undefined,
  atomWithStorageOptions,
);

export const showAiIndicatorsAtom = atomWithStorage<boolean>(
  `${ROOT}_showAiIndicators`,
  true,
  undefined,
  atomWithStorageOptions,
);

export const tutorialsStateAtom = atomWithStorage<typeof defaultTutorialsState>(
  `${ROOT}_tutorialsState`,
  defaultTutorialsState,
  undefined,
  atomWithStorageOptions,
);
